import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

/**
 * El motor de workflow de aprobación — `/api/workflows` del núcleo (`modules/workflow`).
 *
 * Decisión de Juan, 2026-09-28: el workflow define SOLO las personas (qué perfiles, en qué orden, en
 * quién cae cada devolución y quién puede rechazar). Lo que llena cada perfil vive en el tipo de
 * solicitud. Todo responde `{ ok, data }`; el token lo pone `AuthInterceptorService`.
 */
export interface PerfilWorkflow {
  orden: number;
  rol: string;
  rol_codigo?: string;
  rol_nombre?: string;
  devuelve_a: number | null;
  puede_rechazar: boolean;
  plazo_dias?: number | null;
}

export interface PasoRuta {
  orden: number;
  rol: string;
  rol_nombre: string;
  al_aprobar: { tipo: 'PERFIL' | 'FIN'; orden?: number; nombre: string };
  al_devolver: { orden: number; nombre: string } | null;
  puede_rechazar: boolean;
  tarea: string | null;   // lo que le toca a ese perfil en su etapa (del tipo de solicitud)
}

export interface Workflow {
  _id: string;
  tipo_solicitud: string;
  tipo_nombre: string;
  fin: string;
  fin_descripcion?: string;
  nombre: string;
  version: number;
  estado: 'BORRADOR' | 'PUBLICADO' | 'RETIRADO';
  perfiles: PerfilWorkflow[];
  solicitudes_en_curso?: number;
  ruta: PasoRuta[];
  publicado_por: string | null;
  publicado_at: string | null;
  updated_at: string | null;
}

export interface FilaWorkflow {
  tipo_solicitud: string;
  tipo_nombre: string;
  nombre: string;
  publicado: { _id: string; version: number; perfiles: number; publicado_at: string } | null;
  borrador: { _id: string; version: number; perfiles: number; updated_at: string } | null;
  solicitudes_en_curso: number;
  actualizado_at: string | null;
}

export interface RolWorkflow { _id: string; codigo: string; nombre: string; descripcion: string; area: string | null; }
export interface TipoSolicitud { codigo: string; nombre: string; descripcion: string; prefijo: string; fin: string; }
export interface VersionWorkflow { _id: string; version: number; estado: string; publicado_at: string | null; retirado_at: string | null; }

@Injectable({ providedIn: 'root' })
export class WorkflowsService {
  private api = `${environment.BASE_LA}/api/workflows`;

  constructor(private http: HttpClient) { }

  private datos = async <T>(o: any): Promise<T> => (await lastValueFrom(o) as any).data as T;

  listar = () => this.datos<FilaWorkflow[]>(this.http.get(this.api));
  tipos = () => this.datos<TipoSolicitud[]>(this.http.get(`${this.api}/tipos`));
  roles = () => this.datos<RolWorkflow[]>(this.http.get(`${this.api}/roles`));
  yo = () => this.datos<{ puede_configurar: boolean }>(this.http.get(`${this.api}/yo`));
  obtener = (id: string) => this.datos<Workflow>(this.http.get(`${this.api}/${id}`));
  versiones = (id: string) => this.datos<VersionWorkflow[]>(this.http.get(`${this.api}/${id}/versiones`));
  abrirBorrador = (tipo_solicitud: string) => this.datos<Workflow>(this.http.post(this.api, { tipo_solicitud }));
  guardar = (id: string, perfiles: Array<Pick<PerfilWorkflow, 'rol' | 'devuelve_a' | 'puede_rechazar'>>) =>
    this.datos<Workflow>(this.http.put(`${this.api}/${id}`, { perfiles }));
  publicar = (id: string) => this.datos<Workflow>(this.http.post(`${this.api}/${id}/publicar`, {}));
  descartar = (id: string) => this.datos<{ descartado: boolean }>(this.http.delete(`${this.api}/${id}`));
}

/** El mensaje que trae el núcleo, o uno genérico. */
export function mensajeDeError(e: any, porOmision: string): string {
  return e?.error?.error || e?.error?.msg || porOmision;
}
